import * as Sentry from '@sentry/react';

import { loadConfig, loadContextProperties } from "./actions/configActions";
import { toggleEditing } from "./actions/editActions";
import { loadLayout, loadLayoutSuccess } from "./actions/layoutActions";
import { removePageParam } from "./actions/paramsActions";
import { loadConditionValues, userSetLocale, userSetTimeZone } from "./actions/userActions";
import initialState from "./reducers/initialState";
import store from "./store/initStore";
import ConfigUtil from "./util/config/ConfigUtil";
import DateTimeUtil from "./util/DateTimeUtil";
import { ExternalCallbackRegistry } from "./util/IExternalCallback";
import Logger from "./util/Logger";
import { currentMode } from "./util/Mode";
import ResourceUtil from "./util/resource/ResourceUtil";
import TokenSession from "./util/session/TokenSession";
import UrlUtil from "./util/UrlUtil";

/**
 * This script is the post initialization routiene of the appliation.
 *
 * It should be run when:
 * 	1. The redux store is initialized.
 * 	2. All services have loaded.
 *
 * These steps are important to prevent circular dependenceis.
 */

// start the react application.
console.log("Version: " + ConfigUtil.getString("version"));
Logger.debug("Starting....");

// 1. Login the user
TokenSession.init(() => {

	// load remote resources
	ResourceUtil.loadRemoteResources(() => {
		setTimeout(() => {
			ExternalCallbackRegistry.get().callAll(ExternalCallbackRegistry.HOOK_INIT, store.getState());
		}, 5000);
	});

	// 2. remove the token and version params from the url
	store.dispatch(removePageParam("token"));
	store.dispatch(removePageParam("v"));
	store.dispatch(removePageParam("no-load-animation"));

	// 3. dispatch request to get config
	store.dispatch(loadConfig(UrlUtil.getSearchParam("configKey"))).then(() => {
		store.dispatch(loadContextProperties());
		store.dispatch(loadConditionValues());
		store.dispatch(removePageParam("configKey"));
	});


	// 4. dispatch request to get layout
	const inlineLayout = UrlUtil.getSearchParam("layout");

	// see if an inline layout has been provided
	if(inlineLayout) {
		console.log("preparing to use inline layout");
		const layout = JSON.parse(atob(inlineLayout));
		store.dispatch(loadLayoutSuccess(layout));
		console.log(layout);
		store.dispatch(removePageParam("layout"));
	} else {
		store.dispatch(loadLayout());
	}

	// 5. dispatch request to get dictionary for the prefered locale
	const locale = UrlUtil.getSearchParam('lang', TokenSession.getLocale());
	store.dispatch(userSetLocale(locale));

	// 6. set preferred timezone
	store.dispatch(userSetTimeZone(
		DateTimeUtil.guessUserTimezone() || TokenSession.getContext().timeZone
	));

	// 7. put the site in edit mode if the user has permission
	if(TokenSession.editingEnabled()) {
		store.dispatch(toggleEditing(true));
	}

	// send errors to sentry
	if(currentMode.isProd()) {
		console.log("Configuring sentry");
		Sentry.configureScope((scope) => {

			// only setup the user if this is not an anonymous session
			if(TokenSession.isAuthenticated()) {
				const user = TokenSession.getUser();

				scope.setUser({
					id: user.getId().toString(),
					email: user.email,
					token: TokenSession.getToken()
				});
			}

			scope.setTag("context.publicId", TokenSession.getContext().publicId);
		});
	}
});

store.subscribe(() => {
	const state: typeof initialState = store.getState();

	// if the app is loaded the loader / fader to disappear
	if(state.initialization.done) {
		(window as any).appLoaded();
	}

	// trigger the preloaded queries to execute
});



// WEBPACK FOOTER //
// ./src/core/postInit.ts